import {useMutation, useSubscription} from '@apollo/client';
import {useCallback, useState} from 'react';
import {useChat} from '@continuum/ui/hooks';
import type {ChatRepository, ChatStreamEvent} from '@continuum/ui/hooks';
import {
  mapStreamEvent,
  useTurnOrchestrator,
  useStreamRecovery,
} from '@continuum/ui/lib/streaming';
import {
  GENERIC_ERROR_MESSAGE,
  isNotFoundGraphQLError,
} from '~/lib/graphql/errors';
import {ChatStreamUpdates, SendMessage} from '~/domains/canvas/graphql';
import type {
  ChatStreamUpdatesSubscription,
  ChatStreamUpdatesSubscriptionVariables,
} from '~/domains/canvas/graphql/ChatStreamUpdates.graphql';
import type {
  SendMessageMutation,
  SendMessageMutationVariables,
} from '~/domains/canvas/graphql/SendMessage.graphql';

const IGNORED_EVENT_TYPES: Array<ChatStreamEvent['type']> = [
  'TOOL_CALL_EXECUTE',
  'TOOL_CALL_RESULT',
  'TOOL_CALL_DELTA',
];

function useCanvasChatRepository(): ChatRepository {
  const stream = useTurnOrchestrator({
    ignoreEventTypes: IGNORED_EVENT_TYPES,
    // Drop the stream uuid once the server signals the stream is terminal
    // (same as the internal chat hook) so an idle page doesn't keep
    // re-subscribing with a stale uuid on every websocket reconnect.
    onStreamEnd: () => setStreamUuid(null),
  });
  const [streamUuid, setStreamUuid] = useState<string | null>(null);
  const [chatId, setChatId] = useState<string | null>(null);

  const [sendMessageMutation] = useMutation<
    SendMessageMutation,
    SendMessageMutationVariables
  >(SendMessage);

  const {restart} = useSubscription<
    ChatStreamUpdatesSubscription,
    ChatStreamUpdatesSubscriptionVariables
  >(ChatStreamUpdates, {
    variables: {uuid: streamUuid ?? ''},
    skip: !streamUuid || !stream.isStreaming,
    // A restart must reach TransientCableLink and create a fresh subscription,
    // even while Apollo is waiting to tear down the previous listener.
    context: {queryDeduplication: false},
    fetchPolicy: 'no-cache',
    onData: ({data: {data, variables}}) => {
      if (
        (variables as ChatStreamUpdatesSubscriptionVariables | undefined)
          ?.uuid !== streamUuid
      )
        return;
      stream.markPayload();
      const events = data?.chatStreamUpdatesSubscription?.events;
      if (!events) {
        return;
      }
      for (const raw of events) {
        const event = mapStreamEvent(raw);
        if (event) {
          stream.pushEvent(event);
        }
      }
    },
    onError: (err) => {
      if (isNotFoundGraphQLError(err)) {
        // The stream session expired server-side (e.g. an idle shared page
        // re-subscribing long after the stream finished). The stream is
        // already over, so recover silently: drop the stale uuid and end the
        // streaming state; the next message creates a fresh session.
        setStreamUuid(null);
        stream.abort();
        return;
      }

      // Never render raw GraphQL/transport error messages to end users.
      stream.pushEvent({
        type: 'ERROR',
        errorMessage: GENERIC_ERROR_MESSAGE,
        seq: -1,
      });
    },
  });

  useStreamRecovery({
    streamUuid,
    isStreaming: stream.isStreaming,
    getProgress: stream.getStreamProgress,
    restart,
  });

  // A failed send drops its message from the transcript; Retry sends it again.
  const [failedText, setFailedText] = useState<string | null>(null);
  const sendMessage = useCallback(
    (text: string) => {
      if (stream.getTurn().phase !== 'idle') {
        return false;
      }
      setFailedText(null);
      const tempId = stream.addUserMessage({content: text});
      stream.startStreaming();

      sendMessageMutation({
        variables: {message: text, ...(chatId ? {chatId} : {})},
      })
        .then((result) => {
          const data = result.data?.sendChatMessage;
          if (!data) {
            throw new Error('No response from server');
          }
          setChatId(data.chat.id);
          setStreamUuid(data.streamSessionUuid);
        })
        .catch(() => {
          setFailedText(text);
          stream.removeMessage(tempId);
          stream.abort('Failed to send message');
        });
      return true;
    },
    [sendMessageMutation, chatId, stream],
  );

  const abortStream = useCallback(() => {
    setStreamUuid(null);
    stream.abort();
  }, [stream]);

  const retry = useCallback(
    () => (failedText ? sendMessage(failedText) : false),
    [failedText, sendMessage],
  );

  const resetChat = useCallback(() => {
    setFailedText(null);
    setStreamUuid(null);
    setChatId(null);
    stream.resetAll();
  }, [stream]);

  return {
    messages: stream.messages,
    isStreaming: stream.isStreaming,
    isAwaitingResponse: stream.isAwaitingResponse,
    error: stream.error,
    sendMessage,
    retry: failedText ? retry : undefined,
    abortStream,
    resetChat,
  };
}

export function useCanvasChat() {
  const repo = useCanvasChatRepository();
  return useChat(repo);
}
